import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdEdit, MdDeleteOutline } from "react-icons/md";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { IoCalendarOutline, IoTicketOutline } from "react-icons/io5";
import EAModal from "../../../shared/Modal/EAModal";
import EAButton from "../../../shared/Buttons/EAButton";
import EATextField from "../../../shared/TextField/EATextField";
import EASelect from "../../../shared/EASelect/EASelect";
import EABirthDateInput from "../../../shared/BirthDateInput/EABirthDateInput";
import EACheckbox from "../../../shared/Checkbox/EACheckbox";
import EAAlert from "../../../shared/EAAlert";
import { LinkedAccount } from "../../../../models/LinkedAccount";
import { ADD_SUBACCOUNT_URL } from "../../../../api/apiRoutes";
import { State } from "../../../../store";
import useFacility from "../../../../../utils/hooks/useFacility";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import {
  formatBirthDateDdMmYyyy,
  formatSexLabel,
  getLinkedAccountAge,
  getLinkedAccountDisplayName,
  parseBirthDate,
  splitDisplayNameToFirstLast,
} from "../../../../../utils/linkedAccountDisplay";
import { mergeSearchWithTabAndAccountIds } from "../../../../../utils/myAccountTabUrl";
import "../TicketsTab/PassModal/PassModal.scss";
import "./LinkedPersonDetailsModal.scss";
import getId from "../../../../../utils/getId";

interface FormInputs {
  firstName: string;
  lastName: string;
  sex: string;
  birthDate: Date | null;
  agreementNews: boolean;
}

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  account: LinkedAccount | null;
  /** Po udanym zapisie edycji (np. odświeżenie listy). */
  onSaved?: () => void;
  /** Po udanym usunięciu powiązania. */
  onDeleted?: () => void;
}

const LinkedPersonDetailsModal: React.FC<IProps> = ({ isOpen, onClose, account, onSaved, onDeleted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const facilityIds = useFacility();
  const { post, del } = useApiRequest();
  const userEmail = useSelector((state: State) => state.auth.userData?.email ?? "");

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      firstName: "",
      lastName: "",
      sex: "",
      birthDate: null,
      agreementNews: false,
    },
  });

  const sexOptions = useMemo(
    () => [
      { value: "M", label: t("plec_mezczyzna") },
      { value: "K", label: t("plec_kobieta") },
    ],
    [t],
  );

  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
      setApiErrors([]);
      setIsDeleteConfirmOpen(false);
    }
  }, [isOpen]);

  const handleStartEdit = () => {
    if (!account) return;
    const display = getLinkedAccountDisplayName(account);
    const { firstName, lastName } = splitDisplayNameToFirstLast(display);
    reset({
      firstName,
      lastName,
      sex: account.sex === "M" || account.sex === "m" ? "M" : account.sex === "K" || account.sex === "k" ? "K" : "",
      birthDate: parseBirthDate(account.birthDate),
      agreementNews: account.agreementNews === true,
    });
    setApiErrors([]);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setApiErrors([]);
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!account) return;
    setIsDeleting(true);
    try {
      const url = `${ADD_SUBACCOUNT_URL}?facilityIds=${facilityIds}&email=${encodeURIComponent(userEmail)}&accountIds=${account.objectId}`;
      const result = await del(url);
      if (result?.isError) {
        setIsDeleteConfirmOpen(false);
        setApiErrors(result.errorTable ?? [t("linked_person_update_error")]);
      } else {
        setIsDeleteConfirmOpen(false);
        onDeleted?.();
        onClose();
      }
    } catch {
      setIsDeleteConfirmOpen(false);
      setApiErrors([t("linked_person_update_error")]);
    } finally {
      setIsDeleting(false);
    }
  };

  const onSubmitEdit = async (data: FormInputs) => {
    if (!account) return;
    setApiErrors([]);

    if (account.objectId == null || account.objectDb == null) {
      setApiErrors([t("linked_person_update_missing_ids")]);
      return;
    }

    setIsLoading(true);
    const fullName = `${data.firstName.trim()} ${data.lastName.trim()}`.trim();

    const body = {
      facilityIds,
      userMail: userEmail,
      subAccount: {
        objectId: account.objectId,
        objectDb: account.objectDb,
        holderName: fullName,
        holderData1: fullName,
        sex: data.sex,
        birthDate: data.birthDate,
        agreementNews: data.agreementNews,
      },
    };

    try {
      const result = await post(ADD_SUBACCOUNT_URL, body);
      if (result?.isError) {
        setApiErrors(result.errorTable ?? [t("linked_person_update_error")]);
      } else {
        onSaved?.();
        onClose();
      }
    } catch {
      setApiErrors([t("linked_person_update_error")]);
    } finally {
      setIsLoading(false);
    }
  };

  const name = account ? getLinkedAccountDisplayName(account) : "";
  const age = account ? getLinkedAccountAge(account) : null;
  const birth = account ? formatBirthDateDdMmYyyy(account) : "—";
  const sex = account ? formatSexLabel(account.sex ?? null, t) : "—";

  const navigateWithAccountFilter = (tab: "reservations" | "tickets") => {
    if (!account) return;
    const qs = mergeSearchWithTabAndAccountIds(location.search, tab, getId(account));
    navigate(`${location.pathname}?${qs}`);
    onClose();
  };

  return (
    <>
      <EAModal
        isOpen={isOpen && !!account}
        title={isEditing ? t("linked_person_edit_title") : t("szczegoly_konta_osoby")}
        onClose={onClose}
        hideButtons
        desktopWidth="520px"
        onBack={isEditing ? handleCancelEdit : undefined}
      >
        {account && !isEditing && (
          <div className="ticket-details linked-person-details">
            <div className="ticket-details__name-section">
              <span className="body-text-small text-color-light-gray">{t("imie_i_nazwisko")}</span>
              <b className="body-text text-color-dark">{name}</b>
            </div>

            <div className="linked-person-details__summary">
              <div className="ticket-details__grid">
                <div className="ticket-details__item">
                  <span className="body-text-small text-color-light-gray">{t("wiek")}</span>
                  <b className="body-text-small text-color-dark">{age !== null ? age : "—"}</b>
                </div>
                <div className="ticket-details__item">
                  <span className="body-text-small text-color-light-gray">{t("data_urodzenia")}</span>
                  <b className="body-text-small text-color-dark">{birth}</b>
                </div>
                <div className="ticket-details__item" style={{ gridColumn: "1 / -1" }}>
                  <span className="body-text-small text-color-light-gray">{t("plec")}</span>
                  <b className="body-text-small text-color-dark">{sex}</b>
                </div>
              </div>

              <div className="linked-person-details__toolbar">
                <EAButton
                  title={t("edytuj")}
                  variant="outlined"
                  color="primary"
                  compact
                  startIcon={<MdEdit />}
                  onClick={handleStartEdit}
                />
                <EAButton
                  title={"Usuń"}
                  variant="outlined"
                  color="error"
                  compact
                  startIcon={<MdDeleteOutline />}
                  onClick={handleDeleteClick}
                />
              </div>
            </div>

            <div className="linked-person-details__shortcuts">
              <span className="body-text-small text-color-light-gray">
                Zobacz rezerwacje, bilety i karnety tej osoby
              </span>
              <div className="linked-person-details__shortcut-row">
                <EAButton
                  title="Zobacz rezerwacje"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<IoCalendarOutline />}
                  onClick={() => navigateWithAccountFilter("reservations")}
                />
                <EAButton
                  title="Zobacz bilety i karnety"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<IoTicketOutline />}
                  onClick={() => navigateWithAccountFilter("tickets")}
                />
              </div>
            </div>

            <div className="linked-person-details__footer">
              <EAButton title={t("zamknij")} variant="outlined" onClick={onClose} fullWidth />
            </div>
          </div>
        )}

        {account && isEditing && (
          <form
            className="linked-person-details linked-person-details--edit"
            onSubmit={handleSubmit(onSubmitEdit)}
            noValidate
          >
            <div className="linked-person-details__field">
              <EATextField
                label={t("imie")}
                name="firstName"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                register={register("firstName", { required: t("to_pole_jest_wymagane") })}
              />
            </div>
            <div className="linked-person-details__field">
              <EATextField
                label={t("nazwisko")}
                name="lastName"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                register={register("lastName", { required: t("to_pole_jest_wymagane") })}
              />
            </div>
            <div className="linked-person-details__field">
              <Controller
                name="sex"
                control={control}
                rules={{ required: t("to_pole_jest_wymagane") }}
                render={({ field }) => (
                  <EASelect
                    label={t("plec")}
                    name="sex"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    options={sexOptions}
                    placeholder={t("linked_person_plec_placeholder")}
                    fullWidth
                    error={!!errors.sex}
                    helperText={errors.sex?.message}
                  />
                )}
              />
            </div>
            <div className="linked-person-details__field">
              <Controller
                name="birthDate"
                control={control}
                rules={{
                  required: t("to_pole_jest_wymagane"),
                  validate: (val) => {
                    if (!val) return t("to_pole_jest_wymagane");
                    if (isNaN((val as Date).getTime())) return t("to_pole_jest_wymagane");
                    return true;
                  },
                }}
                render={({ field }) => (
                  <EABirthDateInput
                    label={t("data_urodzenia")}
                    name="birthDate"
                    field={field as any}
                    error={!!errors.birthDate}
                    helperText={errors.birthDate?.message}
                    fullWidth
                  />
                )}
              />
            </div>
            <div className="linked-person-details__checkbox">
              <Controller
                name="agreementNews"
                control={control}
                render={({ field }) => (
                  <EACheckbox
                    name="agreementNews"
                    checked={field.value}
                    onChange={(checked) => field.onChange(checked)}
                    label={t("linked_person_newsletter_checkbox")}
                  />
                )}
              />
            </div>

            {apiErrors.length > 0 && <EAAlert type="error" messages={apiErrors} />}

            <div className="linked-person-details__form-actions">
              <EAButton
                title={t("anuluj")}
                variant="outlined"
                fullWidth
                inputType="button"
                onClick={handleCancelEdit}
                disabled={isLoading}
              />
              <EAButton
                title={t("zapisz")}
                variant="filled"
                fullWidth
                inputType="submit"
                loading={isLoading}
                loadingText={t("linked_person_saving")}
              />
            </div>
          </form>
        )}
      </EAModal>

      <EAModal
        isOpen={isDeleteConfirmOpen}
        title="Usuń powiązanie"
        onClose={() => setIsDeleteConfirmOpen(false)}
        acceptButtonLabel="Usuń"
        cancelButtonLabel={t("anuluj")}
        onAccept={handleDeleteConfirm}
        loading={isDeleting}
      >
        <p>
          Czy na pewno chcesz usunąć powiązanie konta osoby <b>{name}</b>? Tej operacji nie można cofnąć.
        </p>
      </EAModal>
    </>
  );
};

export default LinkedPersonDetailsModal;
